import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Step, StepLabel, Typography } from "@mui/material";
import { StyledStepper } from "./styles/StyledComponents";
import HeaderBanner from "./components/HeaderBanner";
import StepCalcola from "./components/steps/StepCalcola";
import StepPersonalizza from "./components/steps/StepPersonalizza";
import StepDatiPersonali from "./components/steps/StepDatiPersonali";
import {
  STEPS,
  INITIAL_VITA_GARANZIE,
  INITIAL_FORM_DATA,
  INITIAL_ERRORS,
  GARANZIE_PREZZI,
} from "./constants";
import { useFormRCAutoDetails } from "../../../rcautoContext";

function TCM() {
  const { formState } = useFormRCAutoDetails();
  const [vitaStep, setVitaStep] = useState(0);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [vitaGaranzie, setVitaGaranzie] = useState(INITIAL_VITA_GARANZIE);

  // Inizializza i campi con i dati del cliente
  useEffect(() => {
    if (formState?.cliente) {
      const cliente = formState.cliente;
      const updates = {};

      // Precompila nome
      if (cliente.nome) updates.nome = cliente.nome;
      
      // Precompila cognome
      if (cliente.cognome) updates.cognome = cliente.cognome;
      
      // Precompila email
      if (cliente.email) updates.email = cliente.email;
      
      // Precompila telefono
      if (cliente.telefono) updates.telefono = cliente.telefono;
      
      // Precompila codice fiscale
      if (cliente.cf) updates.codiceFiscale = cliente.cf;
      
      // Converte e precompila data di nascita da DD/MM/YYYY a YYYY-MM-DD
      if (cliente.dataNascita) {
        let dataNascita = cliente.dataNascita;
        if (typeof dataNascita === "string" && dataNascita.includes("/")) {
          const parts = dataNascita.split("/");
          if (parts.length === 3) {
            dataNascita = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
        updates.dataNascita = dataNascita;
      }

      if (Object.keys(updates).length > 0) {
        setFormData((prev) => ({ ...prev, ...updates }));
      }
    }
  }, [formState?.cliente]);

  const handleCheckboxPrivacyChange = (event) => {
    setFormData({ ...formData, privacyAccettata: event.target.checked });
  };

  const handleCalcolaPreventivo = () => {
    const newErrors = {
      ...errors,
      dataNascita: !formData.dataNascita,
      privacyAccettata: !formData.privacyAccettata,
    };

    setErrors(newErrors);

    if (newErrors.dataNascita || newErrors.privacyAccettata) {
      return;
    }

    setVitaStep(1);
  };

  const handleGaranziaToggle = (garanzia) => (event) => {
    setVitaGaranzie({
      ...vitaGaranzie,
      [garanzia]: {
        ...vitaGaranzie[garanzia],
        enabled: event.target.checked,
      },
    });
  };

  const handleSliderChange = (garanzia, field) => (event, newValue) => {
    setVitaGaranzie({
      ...vitaGaranzie,
      [garanzia]: {
        ...vitaGaranzie[garanzia],
        [field]: newValue,
      },
    });
  };

  const calcolaPremioTotale = () => {
    let totale = 0;
    if (vitaGaranzie.decesso.enabled) {
      totale += GARANZIE_PREZZI.decesso;
    }
    if (vitaGaranzie.invalidita.enabled) {
      totale += GARANZIE_PREZZI.invalidita;
    }
    if (vitaGaranzie.malattiaGrave.enabled) {
      totale += GARANZIE_PREZZI.malattiaGrave;
    }
    if (vitaGaranzie.inabilita.enabled) {
      totale += GARANZIE_PREZZI.inabilita;
    }
    return totale;
  };

  const handleContinuaDatiPersonali = () => {
    setVitaStep(2);
  };

  const handleFinalSubmit = () => {
    const newErrors = {
      ...errors,
      nome: !formData.nome,
      cognome: !formData.cognome,
      email: !formData.email,
      telefono: !formData.telefono,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    console.log("Preventivo TCM/Vita Completo:", {
      formData,
      garanzie: vitaGaranzie,
      premioTotale: calcolaPremioTotale(),
    });

    setVitaStep(0);
  };

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors(INITIAL_ERRORS);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Box sx={{ width: "100%" }}>
        <Helmet title="TCM - Polizza Vita" />

        <HeaderBanner vitaStep={vitaStep} />

        <StyledStepper activeStep={vitaStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>
                <Typography variant="caption">{label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </StyledStepper>

        {vitaStep === 0 && (
          <StepCalcola
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            onPrivacyChange={handleCheckboxPrivacyChange}
            onCalcolaClick={handleCalcolaPreventivo}
            onReset={handleReset}
          />
        )}

        {vitaStep === 1 && (
          <StepPersonalizza
            vitaGaranzie={vitaGaranzie}
            onGaranziaToggle={handleGaranziaToggle}
            onSliderChange={handleSliderChange}
            premioTotale={calcolaPremioTotale()}
            onBack={() => setVitaStep(0)}
            onContinua={handleContinuaDatiPersonali}
          />
        )}

        {vitaStep === 2 && (
          <StepDatiPersonali
            formData={formData}
            errors={errors}
            vitaGaranzie={vitaGaranzie}
            premioTotale={calcolaPremioTotale()}
            onInputChange={handleInputChange}
            onBack={() => setVitaStep(1)}
            onSubmit={handleFinalSubmit}
            onReset={handleReset}
          />
        )}
      </Box>
    </Box>
  );
}

export default TCM;
